// @ts-strict-ignore
import React, {
  useCallback,
  useEffect, useMemo, useRef, useState,
} from 'react';

import { selectExpressionActiveSubEditor, selectFocusedAction, selectWorkflowData } from 'flow/store/redux/selectors';
import { useAppDispatch, useAppSelector } from '~/store/reduxHooks';

import AttributeExpressionGroup from '../../expressions/AttributeExpressionGroup/AttributeExpressionGroup';
import { RuleEditorContext } from '../context';
import { useCaretControl } from '../../useCaretControl/useCaretControl';
import type {
  IsolatedEditingBlockData,
  RuleItemRef,
  RuleItemRefs,
} from '../types';
import type { Action } from '~/dashboard/views/workflows/store/engine/types';
import { MLAEditorContext } from '../../mla/context';
import type { MLASourceData } from '../../mla/types';
import MLAFootnotesContainer from '../../mla/MLAFootnotesContainer/MLAFootnotesContainer';
import { resetNodeAndConnectorState, setExpressionActiveSubEditor } from '~/dashboard/views/workflows/store/redux/slice';
import { useKeyboardKeyEvent } from 'flow/editor/hooks/useKeyboardKeyEvent';
import { getMLAsUsedInExpression } from '~/dashboard/views/workflows/service/mla';
import { extractDataRouteIds } from '~/dashboard/views/workflows/service/dataRoutes/dataRoutes';
import { CaretIndexType } from '../../useCaretControl/constants';
import type { NodeTooltipProps } from '../../Graph/nodes/BaseNode/NodeTooltip/NodeTooltip';
import { dismissShortcut } from 'flow/editor/hooks/useKeyboardKeyEvent/constants';

type OutputAttributeTabProps = {
  action?: Action;
  editable?: boolean;
  ActionHoverTooltip?: (NodeTooltipProps: NodeTooltipProps) => JSX.Element;
};

const DROPDOWN_REF_WIDTH_PADDING = 10;

export default function OutputAttributeExpressionTab({
  action,
  editable = true,
  ActionHoverTooltip,
}: OutputAttributeTabProps) {
  const dispatch = useAppDispatch();
  const { dataRoutes } = useAppSelector(selectWorkflowData);
  const focusedAction = useAppSelector(selectFocusedAction);
  const sourceAction = useMemo(() => (
    action || focusedAction
  ), [action, focusedAction]);

  const ruleEditorActionPublicId = useRef(null);
  const ruleEditorPanelBoxContainerRef = useRef(null);
  const [
    ruleEditorPanelBoxContainerWidth,
    setRuleEditorPanelBoxContainerWidth,
  ] = useState<number>(0);

  const [
    ruleItemRefs,
    setRuleItemRefs,
  ] = useState<RuleItemRefs>({});

  const updateRuleItemRefs = useCallback((refItem: RuleItemRef) => {
    setRuleItemRefs(prevState => ({
      ...prevState,
      [refItem.path]: {
        width: refItem.width,
      },
    }));
  }, []);

  const isolatedEditingBlock = useAppSelector(selectExpressionActiveSubEditor);
  const setIsolatedEditingBlock = useCallback((editingBlock: IsolatedEditingBlockData) => {
    dispatch(setExpressionActiveSubEditor(editingBlock));
  }, [dispatch]);

  const caretControl = useCaretControl({
    expressionData: sourceAction?.output?.expression,
    sourceId: sourceAction?.publicId,
    caretType: CaretIndexType.EXPRESSION,
    ruleEditorPanelBoxContainerRef,
    mlaEditorEnabled: Boolean(isolatedEditingBlock.originType),
  });

  const semanticLineBreakData = useMemo(() => {
    // expression item refs are all properly generated because it matches with flat caret indices length
    const groupedByLines = [{
      width: 0,
      items: [],
      id: 0,
      mlaEditorInThisLine: false,
    }];
    caretControl.ruleEditorFlatCaretIndices.forEach((ref, index) => {
      const matchingRuleItemRef = ruleItemRefs[ref];
      const itemRefWidth = matchingRuleItemRef
        ? matchingRuleItemRef.width
        : 0;
      const itemRefPath = ref;

      const isMLAEditorEnabled = isolatedEditingBlock.originCaretIndex?.length > 0
        && isolatedEditingBlock.originCaretIndex === itemRefPath;
      if (groupedByLines.at(-1).width + itemRefWidth + DROPDOWN_REF_WIDTH_PADDING
        >= ruleEditorPanelBoxContainerWidth) {
        const ruleItemRefArray = Object.entries(ruleItemRefs);
        if (itemRefPath.endsWith('-1!dataRoute') && ruleItemRefArray.length > 0) {
          // if the break point path ends with -1!dataRoute, we artificially pull the one with -0!dataRoute from the groupByLines[last].items
          // and add/push it to the new line with this current scope's `ref`.
          // because from this component/Expression data representation perspective, there is no -1!dataRoute.
          // only the caret/dropdown indices have -1!dataRoute to be able to support cursor caret position for svc attribute.
          groupedByLines.at(-1).items.pop();
          const prevItem = caretControl.ruleEditorFlatCaretIndices[index - 1];
          const matchingPrevRuleItemRef = ruleItemRefs[prevItem];
          groupedByLines.push({
            width: matchingPrevRuleItemRef.width + itemRefWidth + DROPDOWN_REF_WIDTH_PADDING,
            items: [prevItem, itemRefPath],
            mlaEditorInThisLine: isMLAEditorEnabled,
            id: groupedByLines.length + 1,
          });
        } else {
          groupedByLines.push({
            width: itemRefWidth + DROPDOWN_REF_WIDTH_PADDING,
            items: [itemRefPath],
            mlaEditorInThisLine: isMLAEditorEnabled,
            id: groupedByLines.length + 1,
          });
        }
      } else {
        groupedByLines.at(-1).items.push(itemRefPath);
        if (isMLAEditorEnabled) {
          groupedByLines.at(-1).mlaEditorInThisLine = true;
        }
        groupedByLines.at(-1).width += itemRefWidth;
      }
    });

    return {
      groupedByLines,
      lineBreakPointIndices: groupedByLines.map(group => (group.items.at(-1))),
    };
  }, [caretControl, isolatedEditingBlock, ruleItemRefs, ruleEditorPanelBoxContainerWidth]);

  const mlaUsedInExpressions = useMemo(() => getMLAsUsedInExpression({
    dataRoutes,
    sourceAction,
  }), [sourceAction, dataRoutes]);

  const context = React.useMemo(() => ({
    ruleEditorPanelBoxContainerWidth,
    ruleItemRefs,
    updateRuleItemRefs,
    semanticLineBreakData,
    sourceAction,
    mlaUsedInRules: mlaUsedInExpressions,
    isDragItemHoveringOnDropZone: undefined,
    setIsDragItemHoveringOnDropZone: undefined,
    isolatedEditingBlock,
    setIsolatedEditingBlock,
    ...caretControl,
    ActionHoverTooltip,
  }), [
    ruleEditorPanelBoxContainerWidth,
    ruleItemRefs,
    updateRuleItemRefs,
    semanticLineBreakData,
    sourceAction,
    caretControl,
    mlaUsedInExpressions,
    isolatedEditingBlock,
    setIsolatedEditingBlock,
    ActionHoverTooltip,
  ]);

  useEffect(() => {
    const getContainerWidth = () => {
      const elementStyles = window.getComputedStyle(ruleEditorPanelBoxContainerRef?.current, null);
      const widthNumber = elementStyles.getPropertyValue('width').replace(/\D/g, '');
      setRuleEditorPanelBoxContainerWidth(Number(widthNumber));
    };

    getContainerWidth();
    window.addEventListener('resize', getContainerWidth);
    return () => window.removeEventListener('resize', getContainerWidth);
  }, []);

  const mlaEditorPanelBoxContainerRef = useRef(null);
  const [mlaContextSourceData, setMlaContextSourceData] = useState<MLASourceData>({
    type: undefined,
    data: undefined,
    attributeTokenMap: undefined,
  });

  const mlaCaretControl = useCaretControl({
    mlaData: mlaContextSourceData?.attributeTokenMap?.components,
    caretType: CaretIndexType.MLA,
    sourceId: mlaContextSourceData?.attributeTokenMap?.attribute.attribute_id,
    ruleEditorPanelBoxContainerRef: mlaEditorPanelBoxContainerRef,
  });

  const mlaContext = useMemo(() => ({
    sourceData: mlaContextSourceData,
    setSourceData: setMlaContextSourceData,
    mlaEditorPanelBoxContainerRef,
    handleCloseMLAEditor: () => {},
    ...mlaCaretControl,
  }), [mlaContextSourceData, mlaCaretControl]);

  useEffect(() => {
    if (ruleEditorActionPublicId.current !== sourceAction?.publicId) {
      ruleEditorActionPublicId.current = sourceAction.publicId;

      // Avoid clobbering the sub editor when undo/redo needs to toggle actions but still needs the sub editor to be open
      const activeBlockBelongsToSourceAction = extractDataRouteIds(sourceAction.output.expression)
        .includes(isolatedEditingBlock.originPublicId);

      if (!activeBlockBelongsToSourceAction) {
        setIsolatedEditingBlock({
          originPublicId: undefined,
          originType: undefined,
          originCaretIndex: undefined,
        });
      }
    }
  }, [isolatedEditingBlock.originPublicId, setIsolatedEditingBlock, sourceAction]);

  const onEscapeKeyDown = useCallback(() => {
    if (isolatedEditingBlock.originType) {
      setIsolatedEditingBlock({
        originPublicId: undefined,
        originType: undefined,
        originCaretIndex: undefined,
      });
    } else {
      dispatch(resetNodeAndConnectorState());
    }
  }, [dispatch, isolatedEditingBlock]);

  useKeyboardKeyEvent({
    shortcut: dismissShortcut,
    callback: onEscapeKeyDown,
  });

  return (
    <RuleEditorContext.Provider value={context}>
      <MLAEditorContext.Provider value={mlaContext}>
        <div
          ref={ruleEditorPanelBoxContainerRef}
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            rowGap: '3px',
            padding: editable ? 'var(--cl-space-4)' : 'none',
            boxSizing: 'content-box',
          }}
        >
          <AttributeExpressionGroup
            editable={editable}
            expressions={sourceAction.output.expression}
            expressionPath=""
          />
        </div>
        {editable && <MLAFootnotesContainer />}
      </MLAEditorContext.Provider>
    </RuleEditorContext.Provider>
  );
}
